/**
 * Contextual inline "fill down" affordance for the invoice line-item editor.
 *
 * After a user changes a line item's classification cell (category / product
 * & service / tax), we surface this small dismissible inline element directly
 * under that cell: "Apply to all N line items". Clicking it copies the value
 * the user just picked onto every line of the same invoice via the
 * `bulkApply` endpoint (see applyLineItemFieldToAllLines thunk).
 *
 * Deliberately NOT a modal and NOT a permanent per-row button — it appears
 * only right after an edit and auto-hides once applied or dismissed. Think
 * the Google Sheets / Airtable fill-down chip.
 *
 * Purely presentational: the parent owns visibility (when to show it) and the
 * onApply handler (which dispatches the thunk). This keeps it reusable across
 * the QBO / Xero / Excel / Dynamics / Sage editors.
 */
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type Props = {
  /** Total number of line items the value would be applied to. */
  lineCount: number;
  /** Apply the edit to all lines. Should resolve when the bulk apply finishes. */
  onApply: () => Promise<void> | void;
  /** Dismiss the affordance without applying. */
  onDismiss: () => void;
  /** Optional cy hook for tests. */
  dataCy?: string;
};

const ApplyToAllLineItems: React.FC<Props> = ({
  lineCount,
  onApply,
  onDismiss,
  dataCy,
}) => {
  const [status, setStatus] = useState<"idle" | "applying" | "done">("idle");

  // Nothing to fill down to if there is only the one line.
  if (lineCount <= 1) return null;

  const handleApply = async () => {
    if (status === "applying") return;
    setStatus("applying");
    try {
      await onApply();
      setStatus("done");
      // Brief success flash, then auto-hide.
      window.setTimeout(() => onDismiss(), 1200);
    } catch {
      // Error alert is dispatched by the thunk; let the user retry.
      setStatus("idle");
    }
  };

  return (
    <div
      data-cy={dataCy || "apply-to-all-line-items"}
      className="d-inline-flex align-items-center mt-1"
      style={{
        fontSize: "0.75rem",
        lineHeight: 1.2,
        background: "#eef3fe",
        border: "1px solid #c9d8fb",
        borderRadius: "4px",
        padding: "2px 6px",
        color: "#2a4fa0",
        maxWidth: "100%",
      }}
    >
      {status === "done" ? (
        <span className="text-success">
          <FontAwesomeIcon icon="check" className="mr-1" />
          Applied to all {lineCount} line items
        </span>
      ) : (
        <>
          <FontAwesomeIcon icon="arrow-down" className="mr-1" />
          <button
            type="button"
            onClick={handleApply}
            disabled={status === "applying"}
            className="btn btn-link p-0 border-0 align-baseline"
            style={{
              fontSize: "0.75rem",
              color: "#2a4fa0",
              fontWeight: 600,
              textDecoration: "underline",
            }}
          >
            {status === "applying"
              ? "Applying…"
              : `Apply to all ${lineCount} line items`}
          </button>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={onDismiss}
            disabled={status === "applying"}
            className="btn btn-link p-0 border-0 ml-2"
            style={{ fontSize: "0.75rem", color: "#6c757d", lineHeight: 1 }}
          >
            <FontAwesomeIcon icon="times" />
          </button>
        </>
      )}
    </div>
  );
};

export default ApplyToAllLineItems;
